import { Box, Flex, useDisclosure, Collapse } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { SanitizedText } from '@/ui/SanitizedDom';
import { useDateFormat } from '@/hooks';
import { getInterviewTime } from '@/utils/interview-utils';
import { Icon } from '@chakra-ui/react';

const locationLabelMap = {
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  phone_interview: 'Phone Interview',
  onsite: 'Onsite',
  teams: 'Teams',
  hackerrank: 'HackerRank',
};

// Detail row component for label: value format
const DetailRow = ({ label, value }) => {
  if (!value) return null;

  return (
    <Box>
      <KText as="span" size="sm-medium" color="secondary-900">
        {label}:{' '}
      </KText>
      <KText as="span" color="secondary-700">
        {value}
      </KText>
    </Box>
  );
};

// Section render functions
const MessageSection = (data, showMessage) => {
  if (!showMessage || !data?.message) return null;

  const { message = '' } = data;

  return (
    <Flex flexDir="column" gap="12px">
      <KText variant="overhead">Message</KText>
      <SanitizedText html={message || '-'} size="sm-medium" />
    </Flex>
  );
};

const InterviewNameSection = (data) => {
  const { interview_name = '' } = data;

  return <DetailRow label="Interview name" value={interview_name || '-'} />;
};

const CandidateSection = (data) => {
  if (!data?.candidate) return null;
  const { candidate = {} } = data;
  const candidateName = candidate?.name;

  return <DetailRow label="Candidate" value={candidateName || '-'} />;
};

const WhenSection = (data, skipWhen) => {
  const { interview_start_time, interview_end_time, time_zone } = data;
  const { dateFormat, timeFormat } = useDateFormat();

  if (skipWhen) return null;

  const interviewTimeResult = getInterviewTime({
    startTime: interview_start_time,
    endTime: interview_end_time,
    timeFormat: timeFormat,
    dateFormat: dateFormat.label,
    currUserTimezone: time_zone,
  });

  const dateLabel =
    typeof interviewTimeResult === 'string'
      ? interviewTimeResult
      : interviewTimeResult.dateLabel;

  const timeLabel =
    typeof interviewTimeResult === 'string'
      ? ''
      : interviewTimeResult.timeLabel;

  const whenValue = timeLabel ? `${dateLabel}, ${timeLabel}` : dateLabel;

  return <DetailRow label="When" value={whenValue} />;
};

const LocationSection = (data, skipLocation) => {
  const { interview_location_enum } = data;

  if (skipLocation) return null;

  const locationLabel = locationLabelMap[interview_location_enum] || '-';

  return <DetailRow label="Where" value={locationLabel} />;
};

const InterviewersSection = (data, skipInterviewers) => {
  const { interviewers = [] } = data;
  const currUser = useSelector(selectCurrUser);

  if (skipInterviewers) return null;

  // Sort interviewers to show current user first
  const sortedInterviewers = [...interviewers].sort((a, b) => {
    if (a.id === currUser.id) return -1;
    if (b.id === currUser.id) return 1;
    return 0;
  });

  const interviewerNames = sortedInterviewers
    .map((interviewer) =>
      interviewer.id === currUser.id
        ? `${interviewer.name} (You)`
        : interviewer.name
    )
    .join(', ');

  return <DetailRow label="Interviewer(s)" value={interviewerNames || '-'} />;
};

const MeetingRoomsSection = (data, skipMeetingRooms) => {
  if (skipMeetingRooms || !data?.meeting_rooms) return null;

  const { meeting_rooms = [] } = data;

  if (meeting_rooms?.length === 0) return null;

  const meetingRoomNames = meeting_rooms
    .map((room) => room?.name)
    .filter(Boolean)
    .join(', ');

  return <DetailRow label="Meeting room" value={meetingRoomNames} />;
};

const JobSection = (data, skipJob) => {
  if (skipJob || !data?.job_name) return null;

  const { job_name } = data;

  return <DetailRow label="Job name" value={job_name || '-'} />;
};

const StageSection = (data, skipStage) => {
  if (skipStage || !data?.stage) return null;
  const { stage } = data;

  return <DetailRow label="Stage" value={stage?.name || '-'} />;
};

// Section renderer map
const sectionRenderers = {
  message: MessageSection,
  interviewName: InterviewNameSection,
  candidate: CandidateSection,
  when: WhenSection,
  location: LocationSection,
  interviewers: InterviewersSection,
  meetingRooms: MeetingRoomsSection,
  job: JobSection,
  stage: StageSection,
};

const defaultSectionOrder = [
  'interviewName',
  'candidate',
  'when',
  'location',
  'interviewers',
  'job',
  'stage',
  'meetingRooms',
  'message',
];

const MeetingDetails = ({
  data,
  skipInterviewers = false,
  skipMeetingRooms = false,
  skipJob = false,
  showMessage = false,
  skipLocation = false,
  skipStage = false,
  skipWhen = false,
}) => {
  return (
    <Flex flexDir="column" gap="4px" p="24px">
      {defaultSectionOrder.map((sectionKey) => {
        const renderer = sectionRenderers[sectionKey];
        if (!renderer) return null;

        // Handle sections with additional parameters
        if (sectionKey === 'message') {
          return <Box key={sectionKey}>{renderer(data, showMessage)}</Box>;
        }
        if (sectionKey === 'interviewers') {
          return <Box key={sectionKey}>{renderer(data, skipInterviewers)}</Box>;
        }
        if (sectionKey === 'meetingRooms') {
          return <Box key={sectionKey}>{renderer(data, skipMeetingRooms)}</Box>;
        }
        if (sectionKey === 'job') {
          return <Box key={sectionKey}>{renderer(data, skipJob)}</Box>;
        }
        if (sectionKey === 'when') {
          return <Box key={sectionKey}>{renderer(data, skipWhen)}</Box>;
        }
        if (sectionKey === 'location') {
          return <Box key={sectionKey}>{renderer(data, skipLocation)}</Box>;
        }
        if (sectionKey === 'stage') {
          return <Box key={sectionKey}>{renderer(data, skipStage)}</Box>;
        }
        return <Box key={sectionKey}>{renderer(data)}</Box>;
      })}
    </Flex>
  );
};

export const MeetingDetailsContainer = ({
  data,
  isCollapsable = true,
  skipInterviewers = false,
  skipMeetingRooms = false,
  skipJob = false,
  showMessage = false,
  skipLocation = false,
  skipStage = false,
  skipWhen = false,
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  if (isCollapsable) {
    return (
      <Box
        borderRadius="xs"
        borderWidth="1px"
        borderColor="border-color"
        w="380px"
        overflow="hidden"
      >
        <Flex
          onClick={onToggle}
          flex="1"
          p="12px 16px"
          cursor="pointer"
          bg="secondary-25"
          gap="6px"
          alignItems="center"
          borderBottom={isOpen && '1px solid'}
          borderColor="border-color"
        >
          <Icon
            width="16px"
            height="16px"
            cursor="pointer"
            as={isOpen ? ChevronDownIcon : ChevronRightIcon}
            onClick={onToggle}
          />
          <KText variant="heading_4" color="primary-900">
            Details
          </KText>
        </Flex>
        <Collapse in={isOpen} unmountOnExit={false}>
          <MeetingDetails
            data={data}
            skipInterviewers={skipInterviewers}
            skipMeetingRooms={skipMeetingRooms}
            skipJob={skipJob}
            showMessage={showMessage}
            skipLocation={skipLocation}
            skipStage={skipStage}
            skipWhen={skipWhen}
          />
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      borderRadius="xs"
      borderWidth="1px"
      borderColor="border-color"
      w="380px"
    >
      <MeetingDetails
        data={data}
        skipInterviewers={skipInterviewers}
        skipMeetingRooms={skipMeetingRooms}
        skipJob={skipJob}
        showMessage={showMessage}
        skipLocation={skipLocation}
        skipStage={skipStage}
      />
    </Box>
  );
};
